import { useQuery } from "@tanstack/vue-query";
import { authQueries } from "@/domains/auth/queries";
import { useSavePreferenceMutation } from "@/domains/auth/mutations";

export function useDonorAccessToken() {
  return useCookie("donor-access-token", {
    domain: useRuntimeConfig().public.baseDomain.replace(/:\d+$/, ""),
    sameSite: "lax",
    secure: import.meta.env.PROD,
    path: "/",
    maxAge: 60 * 60 * 24 * 7,
  });
}

export function useReferralCode() {
  return useCookie("referral-code");
}

export function useReferralLinkUrl(slug: MaybeRefOrGetter<string>, referralCode: MaybeRefOrGetter<string>) {
  const baseDomain = useRuntimeConfig().public.baseDomain;

  return computed(() => `https://${toValue(slug)}.${baseDomain}/?referral=${toValue(referralCode)}`);
}

export function usePreference<T>(
  params: MaybeRefOrGetter<{
    key: string;
    defaultValue: T;
  }>
) {
  const { data: preferences } = useQuery({
    ...authQueries.preferences(),
  });

  const { mutate } = useSavePreferenceMutation();

  const value = computed({
    get: () => {
      if (!preferences.value) {
        return toValue(params).defaultValue;
      }

      const dbValue = preferences.value.find((p) => p.key === toValue(params).key)?.value;

      let finalValue;
      try {
        finalValue = dbValue ? JSON.parse(dbValue) : toValue(params).defaultValue;
      } catch {
        finalValue = dbValue;
      }

      return finalValue as T;
    },
    set: (newValue: T) => {
      mutate({
        key: toValue(params).key,
        value: typeof newValue === "string" ? newValue : JSON.stringify(newValue),
      });
    },
  });

  return value;
}
